import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Coins, Gift, Inbox, Play, RotateCcw, Send, Shield, Sparkles, Swords, Target, Trophy, Undo2, X, Zap } from "lucide-react";
import { sfx } from "@/audio";
import { api, cleanHandle } from "@/lib/api";
import { Face } from "@/city3d/Hud";
import HarbourRush from "@/city3d/HarbourRush";
import { RULES } from "@/city3d/rush";
import { Btn, Info, Ring, Skel } from "./kit";
import { ACC, dur, fmt, useNow } from "./util";

const CHIPS = [100, 1000, 10000, 100000, 1000000];
const HOUR = 36e5;
const SPARK = ["text-x-rose", "text-x-gold", "text-x-accent"];
const MAIN = "!bg-x-rose !text-white hover:!opacity-90 disabled:!bg-x-raised disabled:!text-x-mute";
const OUT = { won: ["Won", 1], lost: ["Lost"], defended: ["Held", 1], beaten: ["Beaten"], tie: ["Tie"], declined: ["Declined"], expired: ["Expired"], void: ["Refund"] };

const fit = (n, d) => Math.min(Math.max(n, d.min), d.max || Infinity);
const chip = (c) => (c >= 1e6 ? `${c / 1e6}M` : c >= 1000 ? `${c / 1000}K` : c);
const other = (x) => (x.me === "a" ? x.b : x.a);
const scores = (x) => (x.me === "a" ? [x.a_score, x.b_score] : [x.b_score, x.a_score]);
const raced = (x) => x.kind === "quick" && x.me === "b";
const outcome = (x) => {
  if (x.status !== "done") return x.status;
  const won = x.winner === x[x.me].k;
  return x.winner === null ? "tie" : raced(x) ? (won ? "defended" : "beaten") : won ? "won" : "lost";
};
const rule = (d) => `Winner takes both stakes minus ${d.fee_pct}%. They have 24 hours. A tie is refunded and doesn't count.`;

const Left = ({ iso }) => {
  const ms = new Date(iso) - useNow();
  return <Ring value={ms / (24 * HOUR)} size={36} stroke={3} acc={ms > 6 * HOUR ? "rose" : "red"}><span className="text-[11px] font-semibold tabular-nums">{dur(ms).split(" ")[0]}</span></Ring>;
};
const Score = ({ n }) => <span className="flex items-center gap-1 text-xs font-semibold tabular-nums text-x-mute"><Target className="h-3.5 w-3.5 text-x-rose" />{n}</span>;
const Stake = ({ n }) => <span className="flex items-center gap-1 rounded-full bg-x-gold-soft px-2 py-0.5 text-xs font-semibold tabular-nums text-x-gold-ink"><Coins className="h-3 w-3" />{n}</span>;
const Chip = ({ win, children }) => <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${win ? "bg-x-rose-soft text-x-rose-ink" : "bg-x-raised text-x-mute"}`}>{children}</span>;
const Who = ({ p, onVisit }) => p ? (
  <button type="button" disabled={!p.k} onClick={() => onVisit(p)} className="group flex min-w-0 items-center gap-2 text-left transition active:scale-95">
    <Face p={p} size={32} ring={ACC.rose} /><span className="truncate text-sm font-semibold group-enabled:hover:underline">@{p.h}</span>
  </button>
) : <span className="text-sm font-semibold text-x-mute">?</span>;

const Card = ({ title, icon: Icon, right, hot, children, ...p }) => (
    <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.15 } }} transition={{ duration: 0.25 }}
      className={`rounded-[20px] border p-3 ${hot ? "border-x-rose" : "border-x-line"}`} {...p}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-x-rose" strokeWidth={2.25} />
        <h3 className="text-sm font-semibold">{title}</h3>
        {right && <span className="ml-auto flex items-center gap-1.5">{right}</span>}
      </div>
      {children}
    </motion.section>
);
const List = ({ children }) => <ul className="space-y-1.5"><AnimatePresence>{children}</AnimatePresence></ul>;
const Row = ({ i = 0, ...p }) => (
  <motion.li layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0, transition: { delay: Math.min(i, 6) * 0.04, duration: 0.25 } }}
    exit={{ opacity: 0, transition: { duration: 0.15 } }} className="rounded-xl bg-x-raised p-2 transition-colors hover:bg-x-active" {...p} />
);

function Money({ x, o }) {
  if (o === "won") return <span className="flex items-center gap-1 text-sm font-semibold text-x-gold-ink">+{fmt(x.prize)}<Coins className="h-3.5 w-3.5" /></span>;
  if (!x.stake || raced(x)) return null;
  if (o === "lost") return <span className="flex items-center gap-1 text-sm font-semibold text-x-mute">-{fmt(x.stake)}<Coins className="h-3.5 w-3.5" /></span>;
  return (x.me === "a" || o === "tie") && <span className="flex items-center gap-1 text-sm font-semibold text-x-mute"><Undo2 className="h-3.5 w-3.5" />{fmt(x.stake)}</span>;
}

function Burst({ prize }) {
  if (useReducedMotion()) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[90] grid place-items-center overflow-hidden">
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * 2 * Math.PI, r = 110 + (i % 3) * 40;
        return (
          <motion.span key={i} className={`absolute ${SPARK[i % 3]}`} initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
            animate={{ x: Math.cos(a) * r, y: Math.sin(a) * r, scale: [0, 1, 0.4], opacity: [1, 1, 0] }} transition={{ duration: 0.8, ease: "easeOut" }}>
            <Sparkles className="h-5 w-5 fill-current" />
          </motion.span>
        );
      })}
      {prize > 0 && (
        <motion.b className="absolute text-4xl font-semibold tabular-nums text-x-gold" initial={{ y: 0, opacity: 0, scale: 0.7 }}
          animate={{ y: -150, opacity: [0, 1, 1, 0], scale: 1 }} transition={{ duration: 1.6 }}>+{fmt(prize)}</motion.b>
      )}
    </div>
  );
}

function Stakes({ d, uxd, value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {[...new Set(CHIPS.map((c) => fit(c, d)))].map((c) => (
        <Btn key={c} tone="soft" aria-pressed={c === value} disabled={c > uxd} onClick={() => onChange(c)}
          className={`flex-1 !px-0 tabular-nums ${c === value ? "outline outline-2 outline-offset-1 outline-x-rose" : ""}`}>
          {chip(c)}
        </Btn>
      ))}
    </div>
  );
}

function Quick({ d, uxd, busy, onGo }) {
  const { room, max } = d.quick, q = max == null ? d : { ...d, max: Math.min(d.max || Infinity, max) }, done = max != null && max < d.min;
  const [s, setS] = useState(100), stake = fit(s, q), win = Math.floor((stake * d.quick.win_pct) / 100);
  return (
    <Card title="Quick" icon={Zap} right={room != null && <span className="text-xs font-medium tabular-nums text-x-mute" data-testid="quick-room">{fmt(room)} to win today</span>}>
      {d.free && (
        <Btn className={`mb-2.5 flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} data-testid="quick-free" disabled={busy || (room != null && room < d.quick.free_win)} onClick={() => onGo(0)}>
          <Gift className="h-4 w-4" />Free<span className="rounded-full bg-white/25 px-2 text-xs">+{d.quick.free_win}</span>
        </Btn>
      )}
      {done ? <p className="text-sm text-x-mute">The harbour has paid you all it will today. Challenge a player, or come back tomorrow.</p> : <>
        <Stakes d={q} uxd={uxd} value={stake} onChange={setS} />
        <Btn tone={d.free ? "soft" : "primary"} className={`mt-2.5 flex w-full items-center justify-center gap-2 py-2.5 ${d.free ? "" : MAIN}`} data-testid="quick-staked" disabled={busy || stake > uxd} onClick={() => onGo(stake)}>
          {stake > uxd ? <><Coins className="h-4 w-4" />Need {fmt(stake - uxd)}</> : <><Play className="h-4 w-4 fill-current" />{stake}<span className="opacity-60">→</span>{win}</>}
        </Btn>
      </>}
    </Card>
  );
}

function Challenge({ d, uxd, who, busy, onGo, onClose }) {
  const [h, setH] = useState(who.h), [s, setS] = useState(100), ref = useRef();
  const stake = fit(s, d), handle = cleanHandle(h);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, []);
  return (
    <Card ref={ref} title="Challenge" icon={Swords} data-testid="challenge-form"
      right={<><Info text={rule(d)} /><button type="button" onClick={onClose} aria-label="Close" className="text-x-mute transition-colors hover:text-x-ink"><X className="h-4 w-4" /></button></>}>
      <form onSubmit={(e) => { e.preventDefault(); onGo(handle, stake); }} className="space-y-2">
        <div className="hud-well flex items-center gap-2 rounded-xl p-1.5 focus-within:ring-2 focus-within:ring-x-rose">
          {who.a && handle.toLowerCase() === who.k
            ? <Face p={who} size={28} ring={ACC.rose} />
            : <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-x-rose-soft text-sm font-semibold text-x-rose-ink">@</span>}
          <input value={h} onChange={(e) => setH(e.target.value)} maxLength={60} autoFocus={!who.h} placeholder="handle" data-testid="challenge-handle"
            className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-x-mute" />
        </div>
        <Stakes d={d} uxd={uxd} value={stake} onChange={setS} />
        <Btn type="submit" className={`flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} data-testid="challenge-submit" disabled={busy || !handle || stake > uxd}>
          {stake > uxd ? <><Coins className="h-4 w-4" />Need {fmt(stake - uxd)}</> : <><Swords className="h-4 w-4" />{stake}</>}
        </Btn>
      </form>
    </Card>
  );
}

export function DuelsPanel({ econ, challenge, onChanged, onVisit }) {
  const [d, setD] = useState(null), [form, setForm] = useState(null), [play, setPlay] = useState(null);
  const [busy, setBusy] = useState(false), [err, setErr] = useState(null);
  const uxd = econ?.uxd ?? 0;

  const load = useCallback(() => api("/duels").then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => { if (challenge) setForm(challenge); }, [challenge]);

  const act = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      load();
      onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const run = (path, body, stake, title) => act(async () => {
    const r = await api(path, { method: "POST", body });
    setForm(null);
    setPlay({ id: r.duel.id, seed: r.seed, stake, target: r.target, title: title(r) });
  });
  const decline = (x) => act(() => api(`/duels/${x.id}/decline`, { method: "POST" }));
  const finish = (taps) => {
    const id = play.id;
    api(`/duels/${id}/finish`, { method: "POST", body: { taps } })
      .then((r) => r.result, (e) => ({ won: null, text: e.message }))
      .then((result) => {
        if (result.won != null) sfx(result.won ? "win" : "lose");
        setPlay((p) => (p?.id === id ? { ...p, result } : p));
      });
  };
  const close = () => {
    setPlay(null);
    load();
    onChanged();
  };

  return (
    <div className="space-y-2.5" data-testid="duels-panel">
      {err && (
        <motion.div key={err} role="alert" data-testid="duels-error" animate={{ x: [0, -5, 5, -3, 3, 0] }} transition={{ duration: 0.35 }}
          className="rounded-xl bg-x-neg-soft px-3 py-2 text-sm font-medium text-x-neg-ink">{err}</motion.div>
      )}
      {d && d.rules !== RULES ? (
        <Card title="XField was updated" icon={RotateCcw} hot data-testid="duels-stale">
          <p className="mb-2.5 text-sm text-x-mute">Reload to play on the new rules, or your runs won't score right.</p>
          <Btn className={`flex w-full items-center justify-center gap-2 py-2.5 ${MAIN}`} onClick={() => window.location.reload()}>
            <RotateCcw className="h-4 w-4" />Reload
          </Btn>
        </Card>
      ) : d ? (
        <>
          <div className="flex items-center gap-2.5 rounded-2xl border border-x-line px-3 py-2">
            {d.left == null ? <><Zap className="h-5 w-5 fill-current text-x-rose" /><span className="text-sm font-semibold">No limits</span></> : <>
              <Ring value={d.left / d.per_day} size={40} stroke={4} acc="rose"><Zap className="h-4 w-4 fill-current text-x-rose" /></Ring>
              <span data-testid="duels-left"><b className="text-xl">{d.left}</b><span className="text-xs text-x-mute">/{d.per_day}</span></span>
            </>}
            <span className="ml-auto flex items-center gap-1.5"><Trophy className="h-4 w-4 text-x-rose" /><b className="text-xl" data-testid="duels-best">{d.best ? d.best.score : "-"}</b></span>
            <Info text={`Quick: race a saved run. A win pays ${d.quick.win_pct}% of your stake, the free duel ${d.quick.free_win} UXD. What the harbour pays you a day is capped and grows with your worth. Challenge: ${rule(d)}`} />
          </div>
          <AnimatePresence>
            {d.inbox.length > 0 && (
              <Card key="inbox" title="Incoming" icon={Inbox} hot
                right={<span className="grid h-5 min-w-5 place-items-center rounded-full bg-x-rose px-1 text-[11px] font-semibold text-white">{d.inbox.length}</span>}>
                <List>
                  {d.inbox.map((x, i) => (
                    <Row key={x.id} i={i}>
                      <div className="flex items-center gap-2">
                        <Who p={x.a} onVisit={onVisit} />
                        <span className="ml-auto flex items-center gap-2"><Score n={x.a_score} /><Stake n={x.stake} /><Left iso={x.expires_at} /></span>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Btn className={`flex flex-1 items-center justify-center gap-1.5 py-2 ${MAIN}`} data-testid="duel-accept" disabled={busy || x.stake > uxd}
                          onClick={() => run(`/duels/${x.id}/accept`, undefined, x.stake, (r) => `vs @${r.target.h}`)}>
                          {x.stake > uxd ? <><Coins className="h-4 w-4" />Need {fmt(x.stake - uxd)}</> : <><Swords className="h-4 w-4" />Fight</>}
                        </Btn>
                        <Btn tone="soft" className="px-3" data-testid="duel-decline" aria-label="Decline" disabled={busy} onClick={() => decline(x)}><X className="h-4 w-4" /></Btn>
                      </div>
                    </Row>
                  ))}
                </List>
              </Card>
            )}
            <Quick key="quick" d={d} uxd={uxd} busy={busy} onGo={(stake) => run("/duels/quick", { stake }, stake, (r) => `vs @${r.target.h}'s run`)} />
            {form ? (
              <Challenge key={form.k || "new"} d={d} uxd={uxd} who={form} busy={busy} onClose={() => setForm(null)}
                onGo={(h, stake) => run("/duels", { h, stake }, stake, () => `vs @${h}`)} />
            ) : (
              <Btn key="ask" tone="rose" className="flex w-full items-center justify-center gap-2 py-2.5" data-testid="challenge-open" onClick={() => setForm({ h: "" })}>
                <Swords className="h-4 w-4" />Challenge
              </Btn>
            )}
            {d.sent.length > 0 && (
              <Card key="sent" title="Sent" icon={Send}>
                <List>
                  {d.sent.map((x, i) => (
                    <Row key={x.id} i={i}>
                      <div className="flex items-center gap-2">
                        <Who p={x.b} onVisit={onVisit} />
                        <span className="ml-auto flex items-center gap-2"><Score n={x.a_score} /><Stake n={x.stake} /><Left iso={x.expires_at} /></span>
                      </div>
                    </Row>
                  ))}
                </List>
              </Card>
            )}
            {d.recent.length > 0 && (
              <Card key="recent" title="Results" icon={Trophy}>
                <List>
                  {d.recent.map((x, i) => {
                    const p = other(x), o = outcome(x), [label, win] = OUT[o] || [o], [mine, theirs] = scores(x);
                    return (
                      <Row key={x.id} i={i}>
                        <div className="flex items-center gap-2">
                          <Who p={p} onVisit={onVisit} />
                          <span className="ml-auto"><Chip win={win}>{label}</Chip></span>
                        </div>
                        <div className="mt-1 flex items-center gap-3">
                          <span className="flex items-center gap-1 text-xl font-semibold tabular-nums">
                            {raced(x) && <Shield className="h-4 w-4 text-x-rose" />}
                            <span className={win ? "text-x-rose-ink" : ""}>{mine ?? "-"}</span>
                            <span className="text-base text-x-control">:</span>
                            <span className="text-x-mute">{theirs ?? "-"}</span>
                          </span>
                          <Money x={x} o={o} />
                          {x.kind === "challenge" && p && (
                            <Btn tone="soft" className="ml-auto !px-2 py-1.5" data-testid="duel-rematch" aria-label="Rematch" onClick={() => setForm(p)}><RotateCcw className="h-4 w-4" /></Btn>
                          )}
                        </div>
                      </Row>
                    );
                  })}
                </List>
              </Card>
            )}
          </AnimatePresence>
        </>
      ) : !err && <div className="space-y-2.5"><Skel className="h-12" /><Skel className="h-32" /><Skel className="h-10" /></div>}
      {play && createPortal(
        <>
          <HarbourRush key={play.id} seed={play.seed} title={play.title} stake={play.stake} target={play.target} result={play.result} onFinish={finish} onClose={close} />
          {play.result?.won && <Burst key={`win-${play.id}`} prize={play.result.prize} />}
        </>,
        document.body,
      )}
    </div>
  );
}
